import {
  AspectRatioVariant,
  BorderRadius,
  Color,
  FilesPreview,
  Flex,
  getFileThumbnail,
  Icon,
  PreviewFile,
  SkeletonBox,
  Spacing,
  Text,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetBill } from '@features/bills/hooks/useGetBill'

import { getImageClassName } from '../../../../../../utils/getClassName'
import { isApiNotFoundError } from '../../../../../../utils/isApiNotFoundError'
import * as Styled from './styles'

interface ReviewEntryDocumentViewerProps {
  billId?: string
}

export const ReviewEntryDocumentViewer = ({ billId }: ReviewEntryDocumentViewerProps): ReactElement | null => {
  const [previewErrorBillId, setPreviewErrorBillId] = useState<string>()
  const { t } = useTranslation()
  const { data, error, isError, isLoading } = useGetBill(billId, { shouldNotifyOnError: false })
  const bill = data?.bill
  const file = bill?.attachments?.[0]?.file
  const hasPreviewError = previewErrorBillId === billId
  const isUnavailable = isApiNotFoundError(error) || hasPreviewError

  const previewFiles: PreviewFile[] = useMemo(() => {
    if (!file?.downloadUrl) {
      return []
    }

    return [
      {
        src: file.isPdf ? file.downloadUrl : getFileThumbnail(file, 'medium'),
        srcZoom: file.isPdf ? file.downloadUrl : getFileThumbnail(file, 'large'),
        srcDownload: file.downloadUrl,
        id: file.id,
      },
    ]
  }, [file])

  const handlePreviewError = useCallback(() => {
    setPreviewErrorBillId(billId)
  }, [billId])

  if (!billId) {
    return null
  }

  if (isLoading) {
    return (
      <Styled.ReviewEntryDocumentViewerWrapper>
        <SkeletonBox aspectRatio={AspectRatioVariant.billAttachment} />
      </Styled.ReviewEntryDocumentViewerWrapper>
    )
  }

  if (isError || hasPreviewError) {
    return (
      <Styled.ReviewEntryDocumentViewerWrapper>
        <Flex
          alignItems="center"
          flexDirection="column"
          flexGrow={1}
          gap={Spacing.S12}
          justifyContent="center"
          width="100%"
        >
          <Icon aria-hidden color={Color.Grey400} icon="fileXCorner" size="lg" />
          <Text alignment="center" colorVariant="secondary" role="alert" variant="bodySM">
            {isUnavailable
              ? t('review_entries.detail.document.unavailable')
              : t('review_entries.detail.document.load_error')}
          </Text>
        </Flex>
      </Styled.ReviewEntryDocumentViewerWrapper>
    )
  }

  if (!previewFiles.length) {
    return null
  }

  return (
    <Styled.ReviewEntryDocumentViewerWrapper>
      <FilesPreview
        aspectRatio={AspectRatioVariant.billAttachment}
        borderRadius={BorderRadius.R12}
        className={getImageClassName()}
        files={previewFiles}
        fitToHeight
        fitToWidth
        isFloatingActionsView
        margin={Spacing.S16}
        onError={handlePreviewError}
        withDownloadButton
        withRotate
        withShadow
        zoomable
      />
    </Styled.ReviewEntryDocumentViewerWrapper>
  )
}
